import formatQuantity from "@utils/formatQuantity"

import { Table } from "@organisms"
import TableView from "@organisms/ObjectsView/TableView/TableView"

import { PRICEBOOK_TYPE_NAME_MAP } from "@pages/Pricebook/PricebookList/PricebookList"

import { useSubtypeUsageQuery } from "./PricebookTableView.store"
import { PricebookTableViewProps } from "./PricebookTableView.types"

export default function PricebookTableView(props: PricebookTableViewProps) {
    const { pricebookTypeName } = props
    const pricebookType = PRICEBOOK_TYPE_NAME_MAP[pricebookTypeName]

    // Fetch subtype usage data and check if the current pricebook type has subtypes
    const { data: subtypeUsageByType, isLoading: isSubtypeUsageLoading } = useSubtypeUsageQuery()
    const hasSubtypes = subtypeUsageByType ? !!subtypeUsageByType[pricebookType] : false

    return (
        <TableView.Table overrideLoading={isSubtypeUsageLoading}>
            {pricebookType === "part" && (
                <Table.Column<PricebookItem>
                    accessorKey="image_url"
                    header="Image"
                    size={80}
                    cell={({ getValue, row }) => (
                        <Table.Cells.ImageCell src={getValue()} fallbackName={row.original.description} />
                    )}
                    enableSorting={false}
                    enablePinning={true}
                    dataType="other"
                />
            )}
            <Table.Column<PricebookItem>
                accessorKey="description"
                header="Short Description"
                size={224}
                cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                enableSorting={true}
                enablePinning={true}
                dataType="alphabetical"
            />
            <Table.Column<PricebookItem>
                accessorKey="status_label"
                header="State"
                size={128}
                cell={({ getValue }) => <Table.Cells.BadgesCell badges={[getValue()]} />}
                enableSorting={true}
                enablePinning={false}
                dataType="other"
                loadingType="badge"
            />
            {pricebookType !== "tax" && hasSubtypes && (
                <Table.Column<PricebookItem>
                    accessorKey="subtype_label"
                    header="Subtype"
                    size={224}
                    cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="alphabetical"
                />
            )}
            <Table.Column<PricebookItem>
                accessorKey="summary_template"
                header="Summary"
                size={288}
                cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                enableSorting={false}
                enablePinning={false}
                dataType="alphabetical"
            />
            {pricebookType === "service" && (
                <Table.Column<PricebookItem>
                    accessorKey="default_price"
                    header="Rate"
                    size={144}
                    cell={({ getValue, row }) => (
                        <Table.Cells.NumericalDataCell
                            value={Number(getValue())}
                            type="value-hour"
                            unitType={row.original.default_unit_type_label}
                        />
                    )}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                    alignment="right"
                />
            )}
            {(pricebookType === "part" || pricebookType === "other") && (
                <Table.Column<PricebookItem>
                    accessorKey="default_price"
                    header="Price"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.NumericalDataCell value={getValue()} type="currency" />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                    alignment="right"
                />
            )}
            {pricebookType === "discount" && (
                <Table.Column<PricebookItem>
                    accessorKey="default_price"
                    header="Discount"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.NumericalDataCell value={getValue()} type="currency" />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                    alignment="right"
                />
            )}
            {pricebookType === "tax" && (
                <Table.Column<PricebookItem>
                    accessorKey="default_price"
                    header="Tax Rate"
                    size={128}
                    cell={({ getValue }) => (
                        <Table.Cells.NumericalDataCell value={parseFloat(getValue())} type="percentage" />
                    )}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                    alignment="right"
                />
            )}
            {pricebookType !== "discount" && pricebookType !== "tax" && (
                <Table.Column<PricebookItem>
                    accessorKey="cost"
                    header="Cost"
                    size={144}
                    cell={({ getValue, row }) => (
                        <Table.Cells.NumericalDataCell
                            value={getValue()}
                            type="value-hour"
                            unitType={row.original.default_unit_type_label}
                        />
                    )}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                    alignment="right"
                />
            )}
            {pricebookType === "part" && (
                <>
                    <Table.Column<PricebookItem>
                        accessorKey="manufacturer"
                        header="MFG"
                        size={144}
                        cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                        enableSorting={true}
                        enablePinning={false}
                        dataType="other"
                    />
                    <Table.Column<PricebookItem>
                        accessorKey="part_number"
                        header="MFG Part #"
                        size={144}
                        cell={({ getValue }) => <Table.Cells.TextCell content={getValue()} />}
                        enableSorting={true}
                        enablePinning={false}
                        dataType="other"
                    />
                </>
            )}
            {pricebookType === "service" && (
                <Table.Column<PricebookItem>
                    accessorKey="default_job_duration"
                    header="Est. Duration"
                    size={144}
                    cell={({ getValue }) => <Table.Cells.NumericalDataCell type="duration" value={getValue()} />}
                    enableSorting={true}
                    enablePinning={false}
                    dataType="numerical"
                    alignment="right"
                />
            )}
            <Table.Column<PricebookItem>
                accessorKey="labels"
                header="Labels"
                size={240}
                cell={({ getValue }) => (
                    <Table.Cells.BadgesCell
                        badges={(getValue() as PricebookItemLabel[]).map((label: PricebookItemLabel) => label.name)}
                    />
                )}
                enableSorting={false}
                enablePinning={false}
                dataType="other"
                loadingType="badge"
            />
            {pricebookType === "part" && (
                <>
                    <Table.Column<PricebookItem>
                        accessorKey="lowest_inventory_status"
                        header="Stock"
                        size={112}
                        cell={({ getValue, row }) => (
                            <Table.Cells.InventoryStatusCell
                                value={getValue()}
                                trackInventory={row.original.track_inventory}
                            />
                        )}
                        enableSorting={true}
                        enablePinning={false}
                        dataType="other"
                    />
                    <Table.Column<PricebookItem>
                        accessorKey="inventory_level"
                        header="Levels"
                        size={112}
                        cell={({ row }) => (
                            <Table.Cells.TextCell
                                content={
                                    row.original.track_inventory
                                        ? `${formatQuantity(row.original.total_quantity)}/${formatQuantity(row.original.total_quantity_desired)}`
                                        : null
                                }
                            />
                        )}
                        enableSorting={true}
                        enablePinning={false}
                        dataType="numerical"
                    />
                </>
            )}
            <Table.Column<PricebookItem>
                accessorKey="last_modified_at"
                header="Last Modified"
                size={144}
                cell={({ getValue }) => <Table.Cells.DateTimeCell value={getValue()} />}
                enableSorting={true}
                enablePinning={false}
                dataType="timing"
            />
        </TableView.Table>
    )
}
